// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  black,
  white,
  gray01,
  gray04,
  primary,
  gray06,
  gray02,
} from 'presentations/views/styles/color';
import { Role } from 'domains/models/team_user/role';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Avatar, { Group } from '../../atoms/Avatar';
import { transformImageUrl } from 'presentations/assets';
import { formatToDateTime } from 'infras/datetime';
import { ApiKeyResourceItem } from 'presentations/api/schema/api_key';

export interface Props {
  teamUid: string;
  apiKey: ApiKeyResourceItem;
  role: Role;
  onClickMenu?: any;
  isLoading?: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const apiKey = props.apiKey;
  const { name, createdAt, expiresAt, user } = apiKey;

  return (
    <Card data-intercom-target="event-type-list-item">
      <Content>
        <Header>
          <Block>
            <TitleLabel>{t('name')}</TitleLabel>
            <Title>{name}</Title>

            <TitleLabel>{t('expiresAt')}</TitleLabel>
            <Title>
              {expiresAt ? formatToDateTime(expiresAt) : t('neverExpires')}
            </Title>
            <UserList>
              <Invitor>
                {user && (
                  <>
                    <Label>{t('Creator')}</Label>
                    <GroupWrap max={1}>
                      <User
                        src={transformImageUrl(user.profileImage, {
                          width: 32,
                          height: 32,
                        })}
                        alt={user.name || undefined}
                        className={'MuiAvatarGroup-avatar'}
                      >
                        {user.name.charAt(0)}
                      </User>
                    </GroupWrap>
                  </>
                )}
                <Label>{t('CreatedAt')}</Label>
                {formatToDateTime(createdAt, 'Asia/Tokyo')}
              </Invitor>
            </UserList>
          </Block>
        </Header>
      </Content>
      <MenuContainer data-intercom-target="event-type-list-item-menu">
        {props.role <= Role.Admin && (
          <IconButton
            onClick={(e: SyntheticEvent) => props.onClickMenu(e, apiKey)}
          >
            <SettingIcon name="settings" />
          </IconButton>
        )}
      </MenuContainer>
    </Card>
  );
}

export function LoadingComponent() {
  return (
    <Card>
      <Content>
        <Header>
          <Block>
            <TagList>
              <Skeleton width="100px" variant="text" />
            </TagList>
            <Title>
              <Skeleton width="100%" variant="text" />
            </Title>
          </Block>
        </Header>
      </Content>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  position: relative;
  background: white;
  padding: ${spacing(32)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  ${mediaMax.desktop} {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${spacing(24)};
  }
`;

const Content = styled.div`
  width: 100%;
  background: white;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  color: ${gray01};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const TagList = styled.div`
  margin-bottom: ${spacing(8)};
  display: flex;
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
const Block = styled.div`
  width: 100%;
  ${mediaMax.tablet} {
    margin-right: ${spacing(24)};
  }
`;
const TitleLabel = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-bottom: ${spacing(4)};
`;

const Title = styled.div`
  font-size: ${fontSize(14)};
  color: ${black};
  margin-bottom: ${spacing(16)};
`;

const MenuContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;
const SettingIcon = styled(Icon)`
  color: ${gray01};
`;

const SecretLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${primary};
  margin-bottom: ${spacing(16)};
`;

const UserList = styled.div`
  display: flex;
  align-items: center;
`;
const User = styled(Avatar)`
  width: 32px;
  height: 32px;
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    width: 30px;
    height: 30px;
  }

  .MuiAvatarGroup-avatar:first-child {
    margin-left: 0;
  }
`;
const GroupWrap = styled(Group)`
  .MuiAvatar-root {
    width: 32px;
    height: 32px;
    font-size: ${fontSize(14)};
    font-weight: bold;
  }
  margin-right: ${spacing(16)};
`;
const Invitor = styled.div`
  margin-right: ${spacing(24)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    margin-right: ${spacing(16)};
  }
`;
const Label = styled.span`
  margin-right: ${spacing(6)};
  color: ${gray02};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(12)};
`;
